import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'stoic-tools-for-a-stressful-week',
  title: 'Stoicism for Stress: 5 Exercises for a Hard Week',
  description:
    'Calm a heavy week with stoicism for stress: five exercises you can do tonight with a notepad, plus three prompts for Felix, the Stoic Philosopher.',
  category: 'Wellbeing',
  icon: 'landmark',
  publishedAt: '2026-09-16',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'Felix',
  keywords: ['stoicism for stress', 'stoic exercises', 'how to stay calm before an exam'],
  body: `It is Sunday night and the week ahead has a shape you don't like. Maybe it's an exam on Thursday, a deadline on Wednesday, or a presentation on Friday that you keep rehearsing in the shower. You aren't panicking. You're running a low, constant hum of dread, and it eats the hours you need for the work.

This is the kind of week where stoicism for stress earns its keep. Stoicism is an old philosophy, shaped by thinkers like Marcus Aurelius, Epictetus and Seneca, and it was built for ordinary people having hard days. You don't need to read any of it. Here are five of its most practical ideas, in plain language, that you can use this week with nothing but a notepad.

## 1. Stoicism for stress starts with two columns

This is the idea the whole tradition leans on, often called the dichotomy of control. Some things are up to you: how you prepare, how you spend tonight, how you treat people, what you do when you get stuck. Other things are not: how hard the exam is, what your manager thinks, whether the projector works.

Most stress comes from spending energy on the second list. Spend five minutes on it: draw two columns and fill in both. Then look only at the first column and circle one action you can finish today, such as "do 25 minutes of practice questions on the topic I fear most."

This is not about pretending the second column doesn't matter. It's about noticing that worrying about it has never once changed it.

## 2. Picture the worst case, calmly

The Stoics had a habit of imagining what could go wrong ahead of time, on purpose and without drama. It sounds grim, but it works the opposite way you'd expect. A vague fear is scary because it has no edges. A specific one can be examined.

Try it in three steps:

1. Write the worst realistic outcome in one sentence. Not a disaster movie, the honest version: "I blank on the second question and lose marks."
2. Write what you would do next. "I'd skip it, finish the rest, and come back."
3. Write one thing that would help it be less likely. "I'll practice that topic once tomorrow."

Notice what happened. The fear got a plan attached, and a plan is lighter than a feeling.

## 3. Do one thing at a time

A stressful week tempts you to hold everything at once: the outline, the slides, the email you owe, the question you can't answer. Your mind keeps flipping between them, and none of them get your full attention.

Pick the single next task and give it a fixed block, say 25 or 45 minutes. Put your phone in another room. When a new worry shows up, write it on a scrap of paper and go back. The list is safe; you don't have to carry it in your head.

Expect the first few minutes to feel slow, then steadier. If your week involves a lot of studying, [Study Smarter in Finals Week](/blog/study-smarter-finals-week/) has a day-by-day plan that fits this approach.

## 4. End each day with a short review

Many Stoics are described as ending the day by looking back over it. You can borrow the habit in five minutes. Ask yourself three questions:

- What went well today, even a little?
- What would I do differently?
- What is the first thing I'll do tomorrow?

The goal is not self-criticism. It is a calm audit: keep what worked, adjust what didn't, and close the day. Writing down tomorrow's first step also stops the 1 a.m. loop of trying to remember everything.

## 5. Zoom out

When you are deep in a stressful week, the problem fills your whole field of view. Zooming out gives it back its real size. Ask: will this matter in a year? In five? What will I remember about this week?

Sometimes the answer is "yes, it matters." That's fine, and it helps you take it seriously. More often the answer is "a little, and less than it feels like tonight." Either way you come back to the work with a steadier head.

## Practice stoicism for stress with Felix

If you'd like a sparring partner for these exercises, [Chot Buddies](/products/chot-buddies/) has one. Felix is the Stoic Philosopher buddy, built around ancient wisdom for modern challenges. He's useful because the exercises above all work better when you say them out loud to someone who asks a good follow-up question.

Here are three prompts you can paste in. Swap the brackets for your own details.

**Morning plan:**

> I have [an exam / a deadline / a presentation] on [day]. Help me split what's up to me from what isn't, then pick the one thing I should do first today.

**Worst-case walkthrough, when the dread is loud:**

> I'm afraid that [your specific fear]. Walk me through the realistic worst case, what I would do if it happened, and one thing I can do now to make it less likely.

**Evening review:**

> Let's do my evening review. Here's what I did today: [list]. Help me see what went well, what I'd adjust, and what the first step should be tomorrow.

When you're spiraling, ask Felix the zoom-out question: how might this week look a year from now? Let him push back if you're catastrophizing.

If the problem is less about stress and more about whether your plan is any good, our post on [getting honest feedback when everyone says yes](/blog/honest-feedback-when-everyone-says-yes/) shows how to ask Scarlett for the straight version.

## What this is and isn't

Felix is an AI companion. He is not therapy and not medical care, and he can't diagnose anything. Stoic exercises are everyday tools for ordinary pressure, not a treatment for anxiety or low mood.

If stress ever feels overwhelming or unsafe, please talk to a mental health professional or contact your local emergency services. If it's the late-night version of this worry, [Who Do You Talk to at 2am?](/blog/who-do-you-talk-to-at-2am/) is a gentler place to start.

## Start with the small column

You don't need to feel calm to begin. Tonight, draw the two columns, pick one action from the first, and write down tomorrow's first step. That takes ten minutes and changes the shape of the week.

Want company while you do it? Open Chot Buddies (free to start), paste the morning plan prompt above, and let Felix help you pick the first action.
`,
};

export default post;
